import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Bot,
  GraduationCap,
  Building2,
  Trophy,
  Sun,
  Plane,
  Video,
  Compass,
  UserCircle,
} from "lucide-react";

export { PLAY_STORE_URL } from "./aboutContent";

export const socialProofStats = [
  { value: "5+", label: "Countries where learners study" },
  { value: "1,500+", label: "Lessons, quizzes & resources" },
  { value: "20+", label: "Opportunities on Discover" },
  { value: "1,000+", label: "High school learners" },
];

export const partnerLogos = [
  { name: "African Leadership University", image: "Building momentum with/ALU.png" },
  { name: "Yale Young African Scholars", image: "Building momentum with/YYAS.jpg" },
  { name: "Mastercard Foundation", image: "Building momentum with/MasterCardFoundation.png" },
  { name: "United World Colleges", image: "Building momentum with/UWC.jpg" },
  { name: "Yale Young Global Scholars", image: "Building momentum with/YYGS.jpg" },
  { name: "Ashoka University", image: "Building momentum with/Ashoka University.png" },
  { name: "OurMoon", image: "Building momentum with/OurMoon.png" },
];

export const fragmentedExperience = [
  { label: "Courses & videos", detail: "Scattered YouTube playlists and PDFs" },
  { label: "Practice", detail: "Quizzes and past papers in different places" },
  { label: "Scholarships", detail: "Spreadsheets, WhatsApp forwards, dead links" },
  { label: "Summer programs", detail: "Buried in email and social feeds" },
  { label: "Help when stuck", detail: "Generic chatbots that don't know your syllabus" },
];

/** Maps 1:1 to Flutter main tabs */
export const homeEcosystem: {
  title: string;
  icon: LucideIcon;
  impact: string;
}[] = [
  {
    title: "Videos",
    icon: Video,
    impact: "Short lessons and student stories you can watch between classes.",
  },
  {
    title: "Courses",
    icon: BookOpen,
    impact: "Curriculum-aligned courses, quizzes, and flashcards for exam prep.",
  },
  {
    title: "Muzanga",
    icon: Bot,
    impact: "An AI tutor that explains concepts in your curriculum context.",
  },
  {
    title: "Discover",
    icon: Compass,
    impact: "Scholarships, summer schools, and programs published by partners.",
  },
  {
    title: "Profile",
    icon: UserCircle,
    impact: "Your progress, application tools, and account in one place.",
  },
];

export const homeLearnerJourney = [
  "Junior secondary",
  "Senior secondary",
  "National exams",
  "Scholarships & summer programs",
  "University applications",
  "Post High School & early career steps",
];

export const opportunityCategories = [
  {
    title: "High school programs",
    icon: Sun,
    description: "Leadership, enrichment, and academic intensives for secondary students.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/YYAS.jpeg",
    link: "/programs",
  },
  {
    title: "Scholarships",
    icon: GraduationCap,
    description: "Funding pathways listed by universities and organisations on Discover.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/UP.webp",
    link: "/programs",
  },
  {
    title: "Summer schools",
    icon: Sun,
    description: "Short programmes that build skills and confidence before university.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/ask.jpg",
    link: "/programs",
  },
  {
    title: "Exchange & global",
    icon: Plane,
    description: "Cross-border experiences and international opportunities.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/pexels-olia-danilevich-8093039.jpg",
    link: "/programs",
  },
  {
    title: "Capacity building",
    icon: Trophy,
    description: "Workshops and programmes that build practical skills.",
    image: "https://uasrwwdrkxzabpyqqguv.supabase.co/storage/v1/object/public/opportunities/MUN.jpg",
    link: "/programs",
  },
  {
    title: "University pathways",
    icon: Building2,
    description: "Scholarships and programmes from universities recruiting high school talent.",
    image: "https://www.alueducation.com/wp-content/uploads/2024/06/cov-graduation.jpg",
    link: "/programs",
  },
];

export const learningFeatures = [
  "Courses aligned to national curricula",
  "Quizzes that show what you know",
  "Flashcards for active recall",
  "Short video lessons",
  "Progress across your subjects",
  "Student stories from peers",
];

export const communityFeatures = [
  "Study groups and peer chat",
  "Discussions beyond the classroom",
  "Student stories and shared journeys",
  "Friends you meet through the app",
];

export const aiCompanionCapabilities = [
  "Explains concepts step by step",
  "Answers questions in your curriculum context",
  "Helps you work through homework and revision",
  "Available when a human tutor is not",
  "Gives direct facts or guided help depending on what you need",
];

export const impactMetrics = [
  { value: "1,000+", label: "High school learners" },
  { value: "20+", label: "Opportunities on Discover" },
  { value: "5+", label: "Countries represented" },
  { value: "1,500+", label: "Learning resources" },
];

/** Opportunity publishers: same audience as Partner CRM */
export const partnerTypes = [
  {
    title: "Universities",
    body: "Publish scholarships and pathways so high school students find you in Discover.",
  },
  {
    title: "Scholarship & foundation partners",
    body: "Reach motivated secondary learners with clear eligibility and deadlines.",
  },
  {
    title: "Summer schools & programmes",
    body: "List intensives, leadership programmes, and enrichment opportunities.",
  },
  {
    title: "NGOs & opportunity publishers",
    body: "Put placements, exchanges, and capacity-building programmes where students already study.",
  },
];

export const homeTestimonials = [
  {
    name: "Ackim",
    role: "Grade 12 student, Lusaka, Zambia",
    story:
      "I found practice questions, Muzanga, and my study group in one place. For the first time, exam season felt manageable, not chaotic.",
    theme: "Exam prep",
  },
  {
    name: "Florence",
    role: "University student, Accra, Ghana",
    story:
      "A scholarship I never would have found on Google appeared in Discover. Skutopia changed what I thought was possible for my family.",
    theme: "Discover",
  },
  {
    name: "Mr. Banda",
    role: "Physics teacher, Olympia Park Secondary School, Lusaka",
    story:
      "I've watched students go from isolated learners to connected ones. When courses and opportunities live together, confidence follows.",
    theme: "Classroom",
  },
];

export const devicePlatforms = [
  { name: "Mobile", description: "Learn on the bus, between classes, anywhere." },
  { name: "Tablet", description: "Comfortable study sessions with room to think." },
];
